<template>
  <CContainer class="table-container mt-5">
    <transition name="fade">
      <MenssageAlert :messages="messages" />
    </transition>
    <CCol class="px-4 py-4 bg-white rounded-top d-flex justify-content-between table-header">
      <h2 class="text-black font-2xl font-weight-bold">
        {{ $t('automation.list.table.title') }}
      </h2>
      <CButton @click="$router.push({ name: 'automationNew' })" color="primary" class="px-4 py-2 table-header-button align-middle">
        {{ $t('automation.list.table.new') }}
      </CButton>
    </CCol>
    <CCol class="bg-white rounded-bottom p-2">
      <transition name="fade" v-if="showRemoveModal">
        <div class="dataTable__popUp popUp centralize-popup" @click.stop.stop>
          <div class="popUp__body shadow-sm">
            <ConfirmationDelete
              :title="removeAutomationModalTitle"
              :description="removeAutomationModalDescription"
              :loadingConfirm="isLoadingConfirmDelete"
              :customStyleModal="customStyleModal"
              :customStyleConfirmActions="customStyleConfirmActions"
              @confirm="removeAutomation"
              @cancel="cancelRemoveAutomation"
            />
          </div>
        </div>
      </transition>
      <div :class="{ 'disable-table': showRemoveModal }">
        <SenditTable
          class="mt-3"
          :headers="tableHeaders"
          :customClassesTable="senditTableClasses"
          :registers="tableRegisters"
          :loading="isLoadingTable"
          :itemsPerPage="pagination.itemsPerPage"
          :currentPage="pagination.currentPage"
          :totalPages="pagination.totalPages"
          :searchPlaceholder="$t('automation.list.table.searchPlaceholder')"
          :searchTime="1000"
          pagination
          searchable
          :sortable="tableSorter"
          sortByBackend
          translateHeader
          @updatePage="emitUpdatePage"
          @search="emitTableSearch"
          @sorterChange="emitTableSort"
        >
          <template #filters-right>
            <div class="d-flex justify-content-end gap-20">
              <div class="d-flex justify-content-center align-items-center text-black-50">
                <span class="font-weight-bold mr-2">{{ $t('automation.list.table.select.state.value') }}</span>
                <v-select
                  ref="stateSelectRef"
                  class="no-close border-gray-300 rounded select"
                  v-model="stateSelect"
                  item-text="label"
                  item-value="value"
                  :searchable="false"
                  :options="stateSelectOptions"
                />
              </div>
            </div>
          </template>
          <template #name="props">
            <td class="align-middle">
              <div v-if="props.item.name">
                <span class="name-overflow">{{ props.item.name }}</span>
                <CustomTooltip v-if="props.item.errors && props.item.errors.length > 0" placement="right">
                  <CButton class="d-inline-flex py-0 px-2">
                    <CIcon class="text-error" name="notificationAlert" size="custom-size" :height="20" viewBox="0 0 32 32" />
                  </CButton>
                  <template v-slot:content>
                    <CCarousel class="CCarousel" arrows animate>
                      <CCarouselItem v-for="(error, index) in props.item.errors" :key="index">
                        <div class="font-sm">
                          <h1 class="font-sm font-bold mb-2">{{ $t('automation.list.table.row.name.error.title') }}</h1>
                          <div v-html="getAutomationErrorContent(props.item, error)" />
                        </div>
                      </CCarouselItem>
                    </CCarousel>
                  </template>
                </CustomTooltip>
              </div>
            </td>
          </template>
          <template #trigger="props">
            <td class="align-middle">
              <span v-if="props.item.trigger">{{ props.item.trigger }}</span>
            </td>
          </template>
          <template #channel="props">
            <td class="align-middle text-center">
              <CIcon
                v-if="props.item.channel && props.item.channel.icon"
                :name="props.item.channel.icon"
                size="custom-size"
                :height="24"
                viewBox="0 0 32 32"
              />
            </td>
          </template>
          <template #state="props">
            <td class="align-middle">
              <CBadge
                v-if="props.item.state && props.item.state.type !== STATE_TYPES.PENDING_CAMPAIGN_CREATION"
                class="px-3 py-1 text-uppercase"
                :class="{ [props.item.state.classes]: true }"
                >{{ props.item.state.translation }}</CBadge
              >
              <span v-else class="d-block text-center">
                <CSpinner style="width: 1.5rem; height: 1.5rem" color="primary" />
              </span>
            </td>
          </template>
          <template #user="props">
            <td class="align-middle">
              <span v-if="props.item.user" class="font-weight-bold">{{ props.item.user }}</span>
            </td>
          </template>
          <template #actions="props">
            <td class="align-middle">
              <v-select
                v-if="props.item.actions"
                :ref="getActionSelectRef(props.item.workflowId)"
                class="no-close border-gray-300 rounded select"
                :placeholder="$t('automation.list.table.row.actions.placeholder')"
                :options="props.item.actions"
                :searchable="false"
                :disabled="props.item.state.type == STATE_TYPES.PENDING_CAMPAIGN_CREATION"
                @input="(action) => dispatchRowAction(action, props.item.workflowId, getActionSelectRef(props.item.workflowId))"
              >
                <template v-slot:option="option">
                  <span :class="option.icon"></span>
                  <span :class="{ [option.classes]: true }">{{ option.label }}</span>
                </template>
              </v-select>
            </td>
          </template>
          <template #updatedDateTime="props">
            <td class="align-middle">
              <span v-if="props.item.updatedDateTime">{{ props.item.updatedDateTime }}</span>
            </td>
          </template>
        </SenditTable>
      </div>
    </CCol>
  </CContainer>
</template>

<script>
import SenditTable from '@/components/table/SenditTable.vue'
import vSelect from 'vue-select'
import { WORKFLOW_ERROR_TYPE, TRIGGER_TYPES, STATE_TYPES, CHANNEL_TYPES, ACTION_TYPES } from '../constants'
import getAutomationBadge from '../utils/getAutomationBadge'
import { dateFormat, getUserDateFormat } from '@/utils/dateUtil.js'
import { getErrorMessage } from '@/utils/errorHandling.js'
import MenssageAlert from '@/components/MenssageAlert'
import services from '@/services'
import ConfirmationDelete from '@/components/ConfirmationDelete'
import CustomTooltip from '@/components/CustomTooltip.vue'

export default {
  name: 'AutomationListTable',
  components: { SenditTable, vSelect, MenssageAlert, ConfirmationDelete, CustomTooltip },
  props: {
    automationList: {
      type: Array,
      default: () => [],
    },
    isLoadingTable: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      stateSelect: null,
      customTooltipStyle: {
        '--cui-tooltip-bg': 'var(--white)',
      },
      messages: [],
      actionSelectRefDefault: 'actionsSelectRef-',
      selectedRowRef: undefined,
      selectedWorkflowId: undefined,
      showRemoveModal: false,
      senditTableClasses: ['--overflow-hidden'],
      tableSorter: { external: true },
      customStyleModal: 'width: 550px',
      customStyleConfirmActions: 'text-align: end',
      STATE_TYPES: STATE_TYPES,
    }
  },
  created() {
    this.setDefaultFilterValues()
  },
  computed: {
    removeAutomationModalTitle() {
      return `<h1 style="font-size: 24px" class="text-black pb-4 mb-4 border-bottom">${this.$t('automation.list.table.remove.title')}</h1>`
    },
    removeAutomationModalDescription() {
      return `
      <div class="text-black w-75">
        <p class="font-normal mb-2">${this.$t('automation.list.table.remove.title')}</p>
        <ul style="list-style-type: disc;" class="mb-2">
          <li>${this.$t('automation.list.table.remove.description.list.item-01')}</li>
          <li>${this.$t('automation.list.table.remove.description.list.item-02')}</li>
          <li>${this.$t('automation.list.table.remove.description.list.item-03')}</li>
        </ul>
        <p class="font-weight-bold">${this.$t('automation.list.table.remove.description.footer')}</p>
      </div>
      `
    },
    tableRegisters() {
      if (!this.automationList || this.automationList.length == 0) return []
      return this.automationList.map((automationData) => {
        return {
          workflowId: automationData?.id,
          name: automationData?.name,
          trigger: this.getAutomationTrigger(automationData?.workflowJson?.triggers?.values?.[0]?.type),
          channel: this.getAutomationChannel(automationData?.workflowJson?.steps?.values?.[0]?.type),
          state: getAutomationBadge(automationData?.workflowStatus),
          user: automationData?.createdByUserName.trim(),
          updatedDateTime: this.getAutomationUpdatedDateTime(automationData?.updatedDateTime),
          actions: this.getAutomationActions(automationData.workflowStatus),
          errors: automationData?.errors,
          destinations: automationData?.destinations,
        }
      })
    },
    tableHeaders() {
      return [
        { key: 'name', label: this.$t('automation.list.table.header.001'), sorter: true },
        { key: 'trigger', label: this.$t('automation.list.table.header.002'), sorter: false },
        { key: 'channel', label: this.$t('automation.list.table.header.003'), sorter: false },
        { key: 'state', label: this.$t('automation.list.table.header.004'), sorter: false },
        { key: 'user', label: this.$t('automation.list.table.header.005'), sorter: false },
        { key: 'updatedDateTime', label: this.$t('automation.list.table.header.006'), sorter: true },
        { key: 'actions', label: this.$t('automation.list.table.header.007'), sorter: false },
      ]
    },
    stateSelectOptions() {
      return [
        { value: 0, label: this.$t('automation.list.table.select.state.options.001') },
        { value: STATE_TYPES.DRAFT, label: this.$t('automation.list.table.select.state.options.002') },
        { value: STATE_TYPES.ENABLED, label: this.$t('automation.list.table.select.state.options.003') },
        { value: STATE_TYPES.DISABLED, label: this.$t('automation.list.table.select.state.options.004') },
      ]
    },
    lang() {
      return this.$store.state.lang
    },
    isLoadingConfirmDelete() {
      return this.$refs[this.selectedRowRef].mutableLoading
    },
  },
  methods: {
    handleInvalidDestinationError(automationData) {
      const relatedErrorMsg = `<p class="mb-0">${this.$t('automation.list.table.item.error.INVALID_DESTINATION')}</p>`
      let destinationList = '<ul style="list-style: disc; padding-inline-start: 25px;" class="mb-0 mt-2">'

      automationData.destinations.forEach((data) => {
        if (data.workflowId !== automationData.workflowId) return

        destinationList += `<li>${data.countryCode}</li>`
      })
      destinationList += `</ul>`

      return `${relatedErrorMsg}${destinationList}`
    },
    getAutomationErrorContent(automationData, error) {
      const workflowTypeError = error.workflowTypeError

      if (workflowTypeError == WORKFLOW_ERROR_TYPE.INVALID_DESTINATION && automationData.destinations?.length > 0) {
        return this.handleInvalidDestinationError(automationData)
      }

      if (workflowTypeError == WORKFLOW_ERROR_TYPE.INVALID_SCHEMA) {
        return `<p class="mb-0">${this.$t('automation.list.table.item.error.INVALID_SCHEMA')}</p>`
      }

      if (workflowTypeError == WORKFLOW_ERROR_TYPE.INVALID_SEGMENT) {
        return `<p class="mb-0">${this.$t('automation.list.table.item.error.INVALID_SEGMENT')}</p>`
      }

      if (workflowTypeError == WORKFLOW_ERROR_TYPE.INVALID_SENDER) {
        return `<p class="mb-0">${this.$t('automation.list.table.item.error.INVALID_SENDER')}</p>`
      }
    },
    getActionSelectRef(workflowId) {
      return `${this.actionSelectRefDefault}${workflowId}`
    },
    dispatchRowAction(action, workflowId, rowRef) {
      if (!action) return

      this.selectedRowRef = rowRef
      this.selectedWorkflowId = workflowId

      switch (action.value) {
        case ACTION_TYPES.ACTIVATE:
          this.handleToggleActiveAutomation(ACTION_TYPES.DEACTIVATE)
          break
        case ACTION_TYPES.DEACTIVATE:
          this.handleToggleActiveAutomation(ACTION_TYPES.ACTIVATE)
          break
        case ACTION_TYPES.EDIT:
          this.openAutomationWorkflow()
          break
        case ACTION_TYPES.VIEW:
          this.openAutomationWorkflow()
          break
        case ACTION_TYPES.STATISTICS:
          this.statisticsAutomation()
          break
        case ACTION_TYPES.REMOVE:
          this.toggleShowRemoveModal()
          break

        default:
          break
      }
    },
    emitUpdatePage(page) {
      this.$emit('updatePage', page)
    },
    emitTableSearch(filter) {
      this.$emit('searchFilter', filter)
    },
    emitTableSort(sortValue) {
      this.$emit('changeSort', sortValue)
    },
    setDefaultFilterValues() {
      this.stateSelect = this.stateSelectOptions[0]
    },
    handleLangUpdateForSelect() {
      const stateSelectArrPos = this.stateSelect.value
      this.stateSelect = this.stateSelectOptions[stateSelectArrPos]
    },
    getAutomationTrigger(triggerType) {
      switch (triggerType) {
        case TRIGGER_TYPES.DATE:
          return this.$t('automation.list.table.row.trigger.type.date')
        default:
          return ''
      }
    },
    getAutomationChannel(channelType) {
      switch (channelType) {
        case CHANNEL_TYPES.SEND_EMAIL:
          return {
            icon: 'email',
          }
        case CHANNEL_TYPES.SEND_SMS:
          return {
            icon: 'sms',
          }
        default:
          return
      }
    },
    getAutomationUpdatedDateTime(updatedDate) {
      if (!updatedDate) return ''

      const updatedDateTime = dateFormat(updatedDate)
      const userDateTimeFormat = getUserDateFormat(this.$store.state.auth.user.dateTimeFormat)

      return updatedDateTime.toFormat(userDateTimeFormat)
    },
    getAutomationActions(workflowStatus) {
      const automationActions = []

      let excludentActivateAction = null
      if (workflowStatus == STATE_TYPES.ENABLED) {
        excludentActivateAction = { value: ACTION_TYPES.DEACTIVATE, label: this.$t('automation.list.table.row.actions.options.002') }
        automationActions.push({ value: ACTION_TYPES.VIEW, label: this.$t('automation.list.table.row.actions.options.006') })
      }
      if (workflowStatus == STATE_TYPES.DISABLED) {
        excludentActivateAction = { value: ACTION_TYPES.ACTIVATE, label: this.$t('automation.list.table.row.actions.options.001') }
      }

      if (excludentActivateAction) automationActions.push(excludentActivateAction)

      if (workflowStatus == STATE_TYPES.DRAFT || workflowStatus == STATE_TYPES.DISABLED) {
        automationActions.push({ value: ACTION_TYPES.EDIT, label: this.$t('automation.list.table.row.actions.options.003') })
      }

      if (workflowStatus !== STATE_TYPES.DRAFT) {
        automationActions.push({ value: ACTION_TYPES.STATISTICS, label: this.$t('automation.list.table.row.actions.options.004') })
      }

      if (workflowStatus == STATE_TYPES.DRAFT) {
        automationActions.push({
          value: ACTION_TYPES.REMOVE,
          label: this.$t('automation.list.table.row.actions.options.005'),
          classes: ['text-danger'],
        })
      }

      return automationActions.sort((a, b) => a.value - b.value)
    },
    async handleToggleActiveAutomation(actionType) {
      try {
        this.$refs[this.selectedRowRef].toggleLoading()

        actionType == ACTION_TYPES.ACTIVATE
          ? await services.disableAutomation(this.$route.params.organizationId, this.selectedWorkflowId)
          : await services.enableAutomation(this.$route.params.organizationId, this.selectedWorkflowId)
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.$refs[this.selectedRowRef].toggleLoading()
        const refresh = true
        this.endActionCycle(refresh)
      }
    },
    openAutomationWorkflow() {
      this.$router.push({ name: 'automationWorkflow', params: { workflowId: this.selectedWorkflowId }, hash: '#0' })
      this.endActionCycle()
    },
    statisticsAutomation() {
      this.$router.push({ name: 'automationStatistics', params: { workflowId: this.selectedWorkflowId } })
      this.endActionCycle()
    },
    toggleShowRemoveModal() {
      this.showRemoveModal = !this.showRemoveModal
    },
    async removeAutomation() {
      try {
        this.$refs[this.selectedRowRef].toggleLoading()
        const organizationId = this.$route.params.organizationId
        await services.removeAutomation(organizationId, this.selectedWorkflowId)
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.showRemoveModal = false
        this.$refs[this.selectedRowRef].toggleLoading()
        const refresh = true
        this.endActionCycle(refresh)
      }
    },
    cancelRemoveAutomation() {
      this.toggleShowRemoveModal()
      this.endActionCycle()
    },
    endActionCycle(refresh) {
      this.$refs[this.selectedRowRef].clearSelection()
      this.selectedRowRef = undefined
      this.selectedworkflowId = undefined

      if (refresh) {
        this.refreshTable()
      }
    },
    refreshTable() {
      this.$emit('refreshTable')
    },
  },
  watch: {
    stateSelect(newValue, oldValue) {
      if (!oldValue) return
      this.$emit('stateSelect', newValue)
    },
    lang(newLang) {
      if (newLang) {
        // Workaround para impedir bug de atualização de idioma mantendo estado da linguagem anterior
        this.handleLangUpdateForSelect()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.name-overflow {
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
}

.table-container {
  max-width: 1170px;
}
.table-header {
  border-bottom: 1px solid var(--gray-200);
  > .table-header-button::first-letter {
    font-size: 20px;
  }
}
.select {
  min-width: 140px;
}
.gap-20 {
  gap: 20px;
}
.inactive-badge {
  border: 1px solid var(--gray-200);
}

.centralize-popup {
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  color: var(--danger);
}

.disable-table {
  filter: blur(1px);
  pointer-events: none;
}

.carousel {
  display: flex;
  flex-direction: row;
  gap: 10px;
}
.CCarousel::v-deep .carousel-inner {
  flex-grow: 1;
  overflow-y: auto;
  margin-bottom: 0;
  order: 2;
}
.CCarousel::v-deep .carousel-indicators {
  position: relative;
  margin-bottom: 0;
}
.CCarousel::v-deep .carousel-indicators li {
  background-color: var(--primary);
}
.CCarousel::v-deep .carousel-control-next-icon {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath d='M2.75 0l-1.5 1.5L3.75 4l-2.5 2.5L2.75 8l4-4-4-4z'/%3e%3c/svg%3e");
}
.CCarousel::v-deep .carousel-control-next {
  order: 3;
  position: relative;
}
.CCarousel::v-deep .carousel-control-prev-icon {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath d='M5.25 0l-4 4 4 4 1.5-1.5L4.25 4l2.5-2.5L5.25 0z'/%3e%3c/svg%3e");
}
.CCarousel::v-deep .carousel-control-prev {
  order: 1;
  position: relative;
}
</style>
